// ============================================================================
// mainThreadWatchdog.ts — САМОВОССТАНОВЛЕНИЕ PWA при зависании главного потока.
//
// Проблема (жалоба владельца): приложение могло «повиснуть» белым экраном /
// вечной загрузкой — пользователь снимал его только диспетчером Chrome.
//
// Механизм (SRP, чистые функции отделены от обвязки — тестируются без DOM):
//   • БИТ: раз в 5с пишется метка времени `beatAt` (память, не сеть/не диск);
//   • ПРОВЕРКА: отдельный таймер (не завязан на тяжёлый цикл UI) сравнивает
//     «сейчас» с `beatAt`; если бит не обновлялся > 20с — главный поток был
//     заблокирован (после «оттаивания» таймеры выполняются, зависание видно
//     по возрасту метки);
//   • БИТ НЕ МАСКИРУЕТ ЗАВИСАНИЕ: если возраст предыдущего бита уже больше
//     порога, бит НЕ перезаписывает метку, а сразу отдаёт решение (иначе
//     порядок выполнения таймеров после зависания «съедал» бы факт зависания);
//   • ВОССТАНОВЛЕНИЕ: единожды за сессию — метка `watchdog reload` в кольцевой
//     буфер логов, сохранение флага и `location.reload()`;
//   • ЗАЩИТА ОТ ЦИКЛА: 1 автоперезагрузка на сессию (sessionStorage) + запрет
//     повторной в течение 10 минут (localStorage, timestamp);
//   • СЛЕД: при следующем старте событие уходит в Журнал регистрации (категория
//     ui, level warning) с временем срабатывания и возрастом бита; сервер
//     недоступен — остаётся кольцевой буфер и console.
//   • ФОН (hidden) — не зависание: браузер троттлит таймеры, автоперезагрузку
//     не делаем и метку выравниваем при возврате видимости.
//   • DESKTOP (Wails) — автоперезагрузка запрещена (location.reload() ломает
//     мост Wails): там только запись в буфер.
// ============================================================================

import { appendClientLog, readClientLog } from '@/lib/logger';

/** Периодический «бит» главного потока. */
export const BEAT_INTERVAL_MS = 5000;
/** Период независимой проверки живости бита. */
export const CHECK_INTERVAL_MS = 5000;
/** Возраст бита, после которого поток считается зависшим. */
export const STALE_TIMEOUT_MS = 20000;
/** Запрет повторной автоперезагрузки (вкладки/сессии) — 10 минут. */
export const RELOAD_COOLDOWN_MS = 10 * 60 * 1000;

/** Метка в кольцевом буфере логов — по ней ищется след прошлой перезагрузки. */
export const RELOAD_LOG_MARK = 'watchdog reload';
const LOG_SCOPE = 'watchdog';

const SS_RELOADED = 'hydra_wd_reloaded'; // sessionStorage: '1' — перезагружались в этой сессии
const LS_LAST_RELOAD = 'hydra_wd_last_reload'; // localStorage: timestamp автоперезагрузки
const LS_REPORTED = 'hydra_wd_reported'; // localStorage: timestamp последнего отчёта в журнал

// ---------------------------------------------------------------------------
// ЧИСТАЯ ЛОГИКА (тесты — без DOM/таймеров)
// ---------------------------------------------------------------------------

export interface WatchdogInput {
  now: number;
  lastBeatAt: number;
  lastReloadAt: number | null;
  reloadedThisSession: boolean;
  timeoutMs?: number;
  cooldownMs?: number;
}

export type WatchdogDecision =
  | { action: 'alive'; ageMs: number }
  | { action: 'reload'; ageMs: number }
  | { action: 'log-only'; ageMs: number; reason: 'session' | 'cooldown' };

/** Решение по возрасту бита: жив / перезагрузить / только записать в лог. */
export function decideWatchdogAction(input: WatchdogInput): WatchdogDecision {
  const ageMs = Math.max(0, input.now - input.lastBeatAt);
  const timeoutMs = input.timeoutMs ?? STALE_TIMEOUT_MS;
  if (ageMs <= timeoutMs) return { action: 'alive', ageMs };
  if (input.reloadedThisSession) return { action: 'log-only', ageMs, reason: 'session' };
  const cooldownMs = input.cooldownMs ?? RELOAD_COOLDOWN_MS;
  if (input.lastReloadAt !== null && input.now - input.lastReloadAt < cooldownMs) {
    return { action: 'log-only', ageMs, reason: 'cooldown' };
  }
  return { action: 'reload', ageMs };
}

export interface WatchdogLogLike {
  ts: string;
  message: string;
  details?: unknown;
}

export interface WatchdogEvidence {
  /** Время срабатывания watchdog (ms epoch). */
  at: number;
  /** Возраст бита на момент срабатывания (если известен). */
  ageMs: number | null;
  reason: string;
  source: 'log' | 'flag';
}

/**
 * След прошлой автоперезагрузки: записи буфера `watchdog reload` + флаг
 * localStorage. Дубли (флаг и запись об одном срабатывании) склеиваются;
 * всё, что уже отчитано в журнал (`reportedAt`), повторно не отдаётся.
 */
export function collectWatchdogEvidence(
  logs: readonly WatchdogLogLike[],
  lastReloadAt: number | null,
  reportedAt: number | null,
): WatchdogEvidence[] {
  const cutoff = reportedAt ?? -Infinity;
  const out: WatchdogEvidence[] = [];
  for (const entry of logs) {
    if (!entry.message.startsWith(RELOAD_LOG_MARK)) continue;
    const at = Date.parse(entry.ts);
    if (!Number.isFinite(at) || at <= cutoff) continue;
    const details = (entry.details && typeof entry.details === 'object'
      ? entry.details
      : {}) as Record<string, unknown>;
    out.push({
      at,
      ageMs: typeof details.ageMs === 'number' ? details.ageMs : null,
      reason: typeof details.reason === 'string' ? details.reason : 'unknown',
      source: 'log',
    });
  }
  if (lastReloadAt !== null && lastReloadAt > cutoff) {
    // Флаг и запись буфера — одно и то же срабатывание (пишутся в одном тике).
    const duplicate = out.some((e) => Math.abs(e.at - lastReloadAt) <= 2000);
    if (!duplicate) out.push({ at: lastReloadAt, ageMs: null, reason: 'flag', source: 'flag' });
  }
  return out.sort((a, b) => a.at - b.at);
}

// ---------------------------------------------------------------------------
// ОБВЯЗКА (таймеры, хранилища) — зависимости инъектируются для тестов
// ---------------------------------------------------------------------------

export interface WatchdogDeps {
  now: () => number;
  /** Разрешена ли автоперезагрузка (desktop: запрещена — ломает Wails-мост). */
  canReload: () => boolean;
  reload: () => void;
  readLastReloadAt: () => number | null;
  readSessionReloaded: () => boolean;
  markReloaded: (ts: number) => void;
  logEvent: (level: 'info' | 'warn', message: string, details?: unknown) => void;
}

export interface WatchdogController {
  /** «Бит»: обновляет метку; при просроченном бите — сразу решение (не маскирует). */
  beat: () => void;
  /** Независимая проверка живости бита. */
  check: () => void;
  start: () => void;
  stop: () => void;
  beatAt: () => number;
}

function storageGet(area: 'local' | 'session', key: string): string | null {
  try {
    if (area === 'local') {
      return typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
    }
    return typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(key);
  } catch (e) {
    // Хранилище запрещено (приватный режим/политика) — работаем без флагов.
    console.warn('[watchdog] хранилище недоступно', e);
    return null;
  }
}

function storageSet(area: 'local' | 'session', key: string, value: string): void {
  try {
    if (area === 'local') {
      if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
      return;
    }
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, value);
  } catch (e) {
    // Хранилище запрещено — защиту от цикла не сохранить, но не роняем приложение.
    console.warn('[watchdog] запись в хранилище не удалась', e);
  }
}

function storageGetNumber(area: 'local' | 'session', key: string): number | null {
  const raw = storageGet(area, key);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Зависимости по умолчанию (браузер/PWA). */
function defaultDeps(overrides: Partial<WatchdogDeps>): WatchdogDeps {
  return {
    now: () => Date.now(),
    canReload: () => true,
    reload: () => {
      if (typeof window !== 'undefined') window.location.reload();
    },
    readLastReloadAt: () => storageGetNumber('local', LS_LAST_RELOAD),
    readSessionReloaded: () => storageGet('session', SS_RELOADED) === '1',
    markReloaded: (ts) => {
      storageSet('session', SS_RELOADED, '1');
      storageSet('local', LS_LAST_RELOAD, String(ts));
    },
    logEvent: (level, message, details) => appendClientLog(level, LOG_SCOPE, message, details),
    ...overrides,
  };
}

export interface WatchdogOptions extends Partial<WatchdogDeps> {
  timeoutMs?: number;
  cooldownMs?: number;
  beatIntervalMs?: number;
  checkIntervalMs?: number;
}

export function createWatchdog(options: WatchdogOptions = {}): WatchdogController {
  const deps = defaultDeps(options);
  const timeoutMs = options.timeoutMs ?? STALE_TIMEOUT_MS;
  const cooldownMs = options.cooldownMs ?? RELOAD_COOLDOWN_MS;
  const beatIntervalMs = options.beatIntervalMs ?? BEAT_INTERVAL_MS;
  const checkIntervalMs = options.checkIntervalMs ?? CHECK_INTERVAL_MS;

  let beatAt = deps.now();
  /** Решение по зависанию принимается ОДИН раз за жизнь страницы. */
  let handled = false;
  let beatTimer: ReturnType<typeof setInterval> | null = null;
  let checkTimer: ReturnType<typeof setInterval> | null = null;
  let visibilityHandler: (() => void) | null = null;

  function age(now: number): number {
    return Math.max(0, now - beatAt);
  }

  function handleStale(now: number, staleAgeMs: number): void {
    if (handled) return;
    const decision = decideWatchdogAction({
      now,
      lastBeatAt: now - staleAgeMs,
      lastReloadAt: deps.readLastReloadAt(),
      reloadedThisSession: deps.readSessionReloaded(),
      timeoutMs,
      cooldownMs,
    });
    if (decision.action === 'alive') return;
    handled = true;
    beatAt = now;

    if (decision.action === 'log-only') {
      const why = decision.reason === 'session'
        ? 'уже перезагружались в этой сессии'
        : 'в пределах 10 минут после прошлой перезагрузки';
      deps.logEvent('warn', `watchdog stale — перезагрузка не выполнена (${decision.reason}: ${why})`, {
        ageMs: staleAgeMs,
        thresholdMs: timeoutMs,
        reason: decision.reason,
      });
      return;
    }

    if (!deps.canReload()) {
      deps.logEvent('warn', 'watchdog stale — автоперезагрузка отключена (desktop)', {
        ageMs: staleAgeMs,
        thresholdMs: timeoutMs,
        reason: 'disabled',
      });
      return;
    }

    // Флаги — ДО перезагрузки: защита от цикла должна пережить reload.
    deps.markReloaded(now);
    deps.logEvent('warn', `${RELOAD_LOG_MARK} stale age=${staleAgeMs}ms`, {
      ageMs: staleAgeMs,
      thresholdMs: timeoutMs,
      reason: 'stale-beat',
      reloadedAt: now,
    });
    deps.reload();
  }

  function beat(): void {
    const now = deps.now();
    const stale = age(now);
    // Зависание НЕ маскируем: иначе таймер бита, выполнившись первым после
    // «оттаивания», обнулил бы возраст и проверка ничего не увидела.
    if (stale > timeoutMs) {
      handleStale(now, stale);
      return;
    }
    beatAt = now;
  }

  function check(): void {
    // Фоновая вкладка: таймеры троттлятся браузером — это не зависание.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      beatAt = deps.now();
      return;
    }
    const now = deps.now();
    const stale = age(now);
    if (stale > timeoutMs) handleStale(now, stale);
  }

  function start(): void {
    if (beatTimer !== null) return;
    beatTimer = setInterval(beat, beatIntervalMs);
    checkTimer = setInterval(check, checkIntervalMs);
    if (typeof document !== 'undefined') {
      visibilityHandler = () => {
        // Возврат из фона: троттлинг не считается зависанием — выравниваем метку.
        if (document.visibilityState === 'visible') beatAt = deps.now();
      };
      document.addEventListener('visibilitychange', visibilityHandler);
    }
  }

  function stop(): void {
    if (beatTimer !== null) {
      clearInterval(beatTimer);
      beatTimer = null;
    }
    if (checkTimer !== null) {
      clearInterval(checkTimer);
      checkTimer = null;
    }
    if (visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', visibilityHandler);
    }
    visibilityHandler = null;
  }

  return { beat, check, start, stop, beatAt: () => beatAt };
}

// ---------------------------------------------------------------------------
// ИНИЦИАЛИЗАЦИЯ И СЛЕД ДЛЯ ДИАГНОСТИКИ
// ---------------------------------------------------------------------------

let controller: WatchdogController | null = null;

/** Запуск watchdog (один раз за страницу). Вызывать после выбора режима. */
export function initMainThreadWatchdog(options: WatchdogOptions = {}): WatchdogController | null {
  if (typeof window === 'undefined') return null;
  if (controller) return controller;
  controller = createWatchdog(options);
  controller.start();
  appendClientLog('debug', LOG_SCOPE, 'watchdog started', {
    beatMs: BEAT_INTERVAL_MS,
    checkMs: CHECK_INTERVAL_MS,
    timeoutMs: STALE_TIMEOUT_MS,
  });
  return controller;
}

/** Ожидание выбранной базы: без неё eventLog событие не принимает (лаунчер). */
async function waitForBase(timeoutMs: number): Promise<boolean> {
  const { useCenterStore } = await import('@/store/centerStore');
  if (useCenterStore.getState().baseSlug) return true;
  await new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      unsub();
      clearTimeout(timer);
      resolve();
    };
    const unsub = useCenterStore.subscribe(() => {
      if (useCenterStore.getState().baseSlug) finish();
    });
    const timer = setTimeout(finish, timeoutMs);
  });
  return Boolean(useCenterStore.getState().baseSlug);
}

/**
 * След прошлой автоперезагрузки: при старте пишем в Журнал регистрации
 * (ui/warning) время срабатывания и возраст бита + console. Кольцевой буфер
 * уже содержит запись `watchdog reload` (страховка, если сервер недоступен).
 */
export async function reportPreviousWatchdogReload(): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    const evidence = collectWatchdogEvidence(
      readClientLog(300),
      storageGetNumber('local', LS_LAST_RELOAD),
      storageGetNumber('local', LS_REPORTED),
    );
    if (evidence.length === 0) return;

    console.warn('[watchdog] предыдущая сессия перезагрузилась после зависания', evidence);
    // База не выбрана (лаунчер/форма входа) — журнал такое событие не принимает.
    // Отметку «отчитано» НЕ ставим: след остаётся в кольцевом буфере и уйдёт
    // в журнал при следующем старте, когда база уже выбрана.
    const hasBase = await waitForBase(60000);
    if (!hasBase) return;

    // Отчёт отмечаем до отправки: повторный старт не должен дублировать журнал
    // (отправка идёт через durable-очередь eventLog — не теряется офлайн).
    const newest = Math.max(...evidence.map((e) => e.at));
    storageSet('local', LS_REPORTED, String(newest));
    const { eventLog } = await import('@/lib/eventLog');
    for (const item of evidence) {
      await eventLog.warning('ui', 'PWA перезагрузилась автоматически после зависания', {
        entityType: LOG_SCOPE,
        details: JSON.stringify({
          at: new Date(item.at).toISOString(),
          ageMs: item.ageMs,
          reason: item.reason,
          source: item.source,
        }),
      }).catch((e: unknown) => {
        console.warn('[watchdog] журнал регистрации недоступен — след в кольцевом буфере', e);
      });
    }
  } catch (e) {
    // Диагностика не должна мешать старту приложения — след уже в буфере.
    console.warn('[watchdog] отчёт о прошлой перезагрузке не отправлен', e);
  }
}
